<template>
  <!-- Base Drawer -->
  <transition name="slide">
    <div class="base-drawer"
    :class="[
      {'exit-overlay-hovered': exitOverlayHovered && !pauseOverlayAnim}, 
      forceNoScroll ? 'overflow-y-hidden' : 'overflow-y-scroll',
      { 'is-parent-drawer': isParentDrawer, 'is-child-drawer': isChildDrawer }
    ]"
    :style="pauseOverlayAnim ? 'transition: none;' : ''">
      <!-- Takes up the remaining space not covered by the drawer -->
      <div class="exit-overlay-spacer relative w-full"
        ref="exitOverlay"
        @mouseenter="exitOverlayHovered = true"
        @mouseleave="exitOverlayHovered = false, pauseOverlayAnim = false"
        @click="$emit('close')"
        :class="{ 'is-parent-drawer': isParentDrawer }"
        :style="{ height: isParentDrawer ? '18px' : '80px' }"
      >
        <div class="close-drawer-indicator flex items-center gap-3 py-2 pl-2.5 pr-4 shadow-lg
        bg-neutral-900 text-sm text-white rounded-lg transition-opacity duration-300 ease-in-out"
        :class="{
          'opacity-100 cursor-pointer': exitOverlayHovered && !pauseOverlayAnim, 
          'opacity-0 cursor-default': !exitOverlayHovered || pauseOverlayAnim
        }">
          <DownArrowIcon />
          <div>ESC to close {{ closeLabel }}</div>
        </div>
      </div>
      <div
        class="base-drawer--card"
        :class="[{
          'rounded-3xl': !noBottomRounding,
          'rounded-t-3xl': noBottomRounding,
          'h-auto min-h-screen': heightAuto,
          'h-full': !heightAuto && !heightAutoOnly,
          'h-auto': heightAutoOnly,
          'exit-hover-entering': exitOverlayHovered && !pauseOverlayAnim, 
          'exit-hover-leaving': !exitOverlayHovered && !pauseOverlayAnim
        },
        mobileView ? 'left-1.5 right-1.5' : 'left-3 right-3']"
      >
        <!-- Toolbar -->
        <div v-if="!noToolbar" class="base-drawer--card--toolbar"
        :class="{'p-4' : !noPadding }">
          <slot name="toolbar" />
        </div>

        <!-- Content -->
        <div class="base-drawer--card--content">
          <slot name="content" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import DownArrowIcon from './Icons/DownArrowIcon.vue';

export default {
  name: 'BaseDrawer',
  components: {
    DownArrowIcon
  },
  props: {
    noPadding: {
      type: Boolean,
      default: false
    },
    heightAuto: {
      type: Boolean,
      default: false
    },
    heightAutoOnly: {
      type: Boolean,
      default: false
    },
    noBottomRounding: {
      type: Boolean,
      default: false
    },
    noToolbar: {
      type: Boolean,
      default: false
    },
    mobileView: {
      type: Boolean,
      default: false
    },
    closeLabel: {
      type: String,
      default: 'details'
    },
    forceNoScroll: {
      type: Boolean,
      default: false
    },
    isParentDrawer: {
      type: Boolean,
      default: false
    },
    isChildDrawer: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      exitOverlayHovered: false,
      pauseOverlayAnim: false
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getPreventDrawerEscapeKey'])
  },
  watch: {
    isParentDrawer (newVal) {
      if (!newVal) {
        const exitOverlayRef = this.$refs.exitOverlay
        if (!exitOverlayRef) return

        // We pause the exit hover animation when a child drawer is closed (user must reenter exit area or move mouse a threshold amount)
        // This prevents jarring exit animations when closing a child drawer
        this.pauseOverlayAnim = true
        const handleTransitionEnd = () => {
          setTimeout(() => {
            if (!this.exitOverlayHovered) {
              this.pauseOverlayAnim = false
            } else {
              // If the user is still hovering over the exit area, track their mouse movement
              // If they move their mouse enough, we unpause the animation
              let initialMouseX, initialMouseY
              const trackMouseMovement = (event) => {
                if (!initialMouseX || !initialMouseY) {
                  initialMouseX = event.clientX
                  initialMouseY = event.clientY
                  return
                }
                const deltaX = Math.abs(event.clientX - initialMouseX)
                const deltaY = Math.abs(event.clientY - initialMouseY)
                if (deltaX >= 24 || deltaY >= 24) {
                  this.pauseOverlayAnim = false
                  exitOverlayRef.removeEventListener('mousemove', trackMouseMovement)
                }
              }
              exitOverlayRef.addEventListener('mousemove', trackMouseMovement)
            }
          }, 50)
          exitOverlayRef.removeEventListener('transitionend', handleTransitionEnd)
        }
        exitOverlayRef.addEventListener('transitionend', handleTransitionEnd)
      }
    }
  },
  mounted () {
    window.addEventListener('keydown', this.handleCloseKeydown)
  },
  beforeDestroy () {
    // Cleanup the event listener when component is unmounted
    window.removeEventListener('keydown', this.handleCloseKeydown)
  },
  methods: {
    handleCloseKeydown (event) {
      if (!this.getPreventDrawerEscapeKey && !this.isParentDrawer && (event.key === 'Escape' || event.keyCode === 27)) {
        this.$emit('close')
      }
    }
  }
}
</script>

<style scoped lang="sass">
.base-drawer
  @apply h-screen fixed right-0 top-0 w-screen

  &--card
    animation: appearBottom 300ms ease-in-out
    transition: transform 300ms ease-in-out
    @apply absolute bg-white

    &--toolbar
      @apply flex justify-end items-center

      i
        @apply cursor-pointer text-gray-400 text-lg

    &.exit-hover-entering
      transform: translateY(20px)

    &.exit-hover-leaving
      transform: translateY(0px)

@keyframes appearBottom
  0%
    opacity: 0
    transform: translateY(400px)

  100%
    opacity: 1

.slide-leave-active
  transition: 300ms

.slide-leave-to
  opacity: 0
</style>

<style scoped lang="css">
.base-drawer {
  background: rgba(0, 0, 0, 0.75);
  z-index: 50000;
  transition: background 0.3s ease-in-out;
}
.base-drawer.exit-overlay-hovered {
  background: rgba(0, 0, 0, 0.5);
}
.base-drawer.is-parent-drawer {
  /* Force the parent to use a lower opacity bg without transitions */
  background: rgba(0, 0, 0, 0.5);
  transition: none;
}
.base-drawer.is-child-drawer {
  background: rgba(0, 0, 0, 0.5);
}
.base-drawer.is-child-drawer.exit-overlay-hovered {
  background: rgba(0, 0, 0, 0.25);
}

.close-drawer-indicator {
  position: absolute;
  top: 38px;
  left: 50%;
  transform: translateX(-50%);
}
.exit-overlay-spacer {
  transition: height 300ms ease-in-out;
}
.exit-overlay-spacer.is-parent-drawer {
  transition: height 150ms 150ms ease-out;
}
</style>